import { cloneDeep } from "lodash";

import type { DocumentNode, QueryHookOptions } from "@apollo/client";

import { AuthenticatedClientConfig } from "../../../common/config/config";

import { STR_ID } from "./constants";

import getLogger from "$common/log";
import { NavConfig } from "$common/nav-config";
import { client } from "$graphql/client/client-side-provider";
import { HAMBURGER_MENU_NEW_QUERY } from "$graphql/core/queries/hamburger-menu";

export type PostData = {
    ApplicationId: number;
    Culture: string;
    MarketingBrandId: number;
};

/**
 * Stub type for the bff API response. This is a placeholder for
 * the `CoStar.Api.SiteNavigation.ViewModels.Menu` type exposed by
 * `@costar-gen/pds-node-client`
 */
export interface SiteNavigationMenu {
    MenuId: number;
    MarketingBrandId: number;
    ApplicationId: number;
    ViewName: string;
    IsActivated: boolean;
    MenuItems: unknown[]; // MenuItem[]
    MenuAttributes: unknown[]; // MenuAttribute[]
    MenuAttributesDisplay: unknown[]; // MenuAttributeDisplay[]
    MenuItemGroups?: unknown[]; // MenuGroupAttribute[]
    NotActivated?: string[]; // List of ids - not exactly product ids, but similar
}

const queryPromise = (query: DocumentNode, options: QueryHookOptions) => {
    return client.query({
        query,
        variables: options.variables,
    });
};

const completeHamburgerMenu = (
    menuItems,
    firstName: string,
    lastName: string,
    photoUrl: string
): SiteNavigationMenu => {
    return {
        MenuId: 41,
        MarketingBrandId: 1,
        ApplicationId: 2,
        ViewName: "GenericMenu_2_enUS_Id",
        IsActivated: true,
        MenuItems: menuItems,
        MenuAttributes: [
            {
                MenuAttributeId: 267,
                MenuId: 41,
                MenuAttributeName: "hasSignOutInfo",
                MenuAttributeValueType: null,
                MenuAttributeValue: "True",
                __typename: "MenuAttribute",
            },
        ],
        MenuAttributesDisplay: [
            {
                MenuAttributeDisplayId: 26,
                MenuId: 9,
                Culture: "en-US",
                DisplayAttributeName: "SignOutLabel",
                DisplayAttributeValue: "Sign Out",
                __typename: "MenuAttributeDisplay",
            },
            {
                MenuAttributeDisplayId: 0,
                MenuId: 0,
                Culture: null,
                DisplayAttributeName: "FirstName",
                DisplayAttributeValue: firstName,
                __typename: "MenuAttributeDisplay",
            },
            {
                MenuAttributeDisplayId: 0,
                MenuId: 0,
                Culture: null,
                DisplayAttributeName: "LastName",
                DisplayAttributeValue: lastName,
                __typename: "MenuAttributeDisplay",
            },
            {
                MenuAttributeDisplayId: 0,
                MenuId: 0,
                Culture: null,
                DisplayAttributeName: "UserPhoto",
                DisplayAttributeValue: photoUrl,
                __typename: "MenuAttributeDisplay",
            },
        ],
    };
};

export const makeFetchNewMenuCallback = (config: AuthenticatedClientConfig, navConfig: NavConfig) =>
    async function fetchMenuCallback(postData: PostData): Promise<SiteNavigationMenu> {
        const logger = getLogger();

        const response = await queryPromise(HAMBURGER_MENU_NEW_QUERY, {
            variables: {
                Culture: postData.Culture,
            },
        });

        if (response.error) {
            const error = new Error(
                `Failed to get new hamburger menu data: ${response.error.name} ${response.error.message}`
            );
            Object.assign(error, { status: response.error.name, message: response.error.message });
            logger.error(`Error in fetching hamburger menu data. Message: ${response.error.message}`);
            logger.error(response.error.message);
            throw error;
        }

        if (response && response?.data?.user?.hamburgerMenuNew?.hamburgerMenu) {
            const hamburgerMenuData = response.data.user.hamburgerMenuNew;
            const menu = completeHamburgerMenu(
                hamburgerMenuData.hamburgerMenu,
                hamburgerMenuData.userInfo?.firstName,
                hamburgerMenuData.userInfo?.lastName,
                hamburgerMenuData.userInfo?.photoUri
            );
            // Cloning here because UniversalMenu adds keys to this object. This object is non-extensible so keys cannot be added
            // The only solution is to not use the object and use its clone instead. This is probably because of React.StrictMode
            const menuClone = cloneDeep(menu);
            return menuClone;
        } else {
            return {} as SiteNavigationMenu;
        }
    };
